import { ORDER_STATUS_LABELS } from '../constants';

// Tarih ve saat string/Timestamp/Date nesnesini { date: 'YYYY-MM-DD', time: 'HH:mm' } şeklinde ayırır
export const splitDateTime = (dateValue) => {
  if (!dateValue) return { date: '', time: '' };

  // Eğer string ise timezone kaymasını önlemek için doğrudan regex ile ayır
  if (typeof dateValue === 'string') {
    const trimmed = dateValue.trim();
    const dtMatch = trimmed.match(/^(\d{4}-\d{2}-\d{2})[T\s](\d{2}:\d{2})/);
    if (dtMatch) {
      return {
        date: dtMatch[1],
        time: dtMatch[2]
      };
    }
    const dMatch = trimmed.match(/^(\d{4}-\d{2}-\d{2})/);
    if (dMatch) {
      return {
        date: dMatch[1],
        time: ''
      };
    }
  }

  let d;
  if (dateValue && typeof dateValue.toDate === 'function') {
    d = dateValue.toDate();
  } else if (dateValue instanceof Date) {
    d = dateValue;
  } else {
    d = new Date(dateValue);
  }

  if (isNaN(d.getTime())) {
    return { date: '', time: '' };
  }

  const year = d.getFullYear();
  const month = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  const hours = String(d.getHours()).padStart(2, '0');
  const minutes = String(d.getMinutes()).padStart(2, '0');

  const time = (d.getHours() !== 0 || d.getMinutes() !== 0) ? `${hours}:${minutes}` : '';

  return {
    date: `${year}-${month}-${day}`,
    time
  };
};

// Tarih ve saat parçalarını birleştirir ('YYYY-MM-DDTHH:mm' veya 'YYYY-MM-DD')
export const combineDateTime = (datePart, timePart) => {
  const d = (datePart || '').trim();
  const t = (timePart || '').trim();
  if (!d && !t) return '';
  if (!d && t) {
    const today = new Date();
    const year = today.getFullYear();
    const month = String(today.getMonth() + 1).padStart(2, '0');
    const day = String(today.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}T${t}`;
  }
  if (d && t) {
    return `${d}T${t}`;
  }
  return d;
};

// Tarih değerini normalize eder (string formatına)
export const normalizeDateTime = (dateValue) => {
  if (!dateValue) return '';
  const { date, time } = splitDateTime(dateValue);
  return combineDateTime(date, time);
};

// Date formatting - Firestore Timestamp, Date ve ISO/datetime stringlerini destekler
export const formatDate = (dateValue, showTime = true) => {
  if (!dateValue) return '-';

  const { date, time } = splitDateTime(dateValue);
  if (!date) return '-';

  const parts = date.split('-');
  if (parts.length !== 3) return '-';

  const formattedDate = `${parts[2]}.${parts[1]}.${parts[0]}`;
  if (showTime && time) {
    return `${formattedDate} ${time}`;
  }
  return formattedDate;
};

// Tarih değerini input[type="date"] için YYYY-MM-DD formatına çevirir
export const formatDateForInput = (dateValue) => {
  if (!dateValue) return '';
  const { date } = splitDateTime(dateValue);
  return date || '';
};

// Price formatting - Türk para birimi formatı
export const formatPrice = (price) => {
  const num = parseFloat(price || 0);

  // Türk para birimi formatı: 1.234,56
  return num.toLocaleString('tr-TR', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  });
};

// Validation functions
export const validateEmail = (email) => {
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return emailRegex.test(email);
};

export const validatePhone = (phone) => {
  const phoneRegex = /^[0-9\s\-+()]{8,}$/;
  return phoneRegex.test(phone);
};

/**
 * Veritabanında tam saklanan telefon numarasını (örn. "31612345678" veya "905321234567")
 * alan kodu (dialCode) ve ulusal numara (nationalNumber) olarak ayırır.
 */
export const splitPhoneNumber = (fullPhone = '', defaultDial = '31') => {
  if (!fullPhone) {
    return { dialCode: defaultDial, nationalNumber: '' };
  }
  const clean = String(fullPhone).replace(/\D/g, '');
  if (!clean) {
    return { dialCode: defaultDial, nationalNumber: '' };
  }

  // Bilinen yaygın alan kodlarını öncelik sırasına göre kontrol et (3 hane -> 2 hane -> 1 hane)
  const knownDials = ['31', '90', '32', '49', '33', '44', '1', '7', '43', '41', '39', '34', '30'];
  for (const kd of knownDials) {
    if (clean.startsWith(kd) && clean.length > kd.length) {
      return {
        dialCode: kd,
        nationalNumber: clean.slice(kd.length)
      };
    }
  }

  // Eğer eşleşmezse ve defaultDial ile başlıyorsa
  if (clean.startsWith(defaultDial) && clean.length > defaultDial.length) {
    return {
      dialCode: defaultDial,
      nationalNumber: clean.slice(defaultDial.length)
    };
  }

  return {
    dialCode: defaultDial,
    nationalNumber: clean
  };
};

/**
 * Ulusal telefon numarasını alan koduna ve uzunluğa göre okunabilir formatlar.
 */
export const formatNationalPhoneNumber = (nationalNumber = '', dialCode = '31') => {
  if (!nationalNumber) return '';
  const digits = String(nationalNumber).replace(/\D/g, '').slice(0, 12);

  // Türkiye (+90): 5XX XXX XX XX (10 hane)
  if (dialCode === '90') {
    if (digits.length <= 3) return digits;
    if (digits.length <= 6) return `${digits.slice(0, 3)} ${digits.slice(3)}`;
    if (digits.length <= 8) return `${digits.slice(0, 3)} ${digits.slice(3, 6)} ${digits.slice(6)}`;
    return `${digits.slice(0, 3)} ${digits.slice(3, 6)} ${digits.slice(6, 8)} ${digits.slice(8, 10)}`;
  }

  // Hollanda (+31): Genellikle 06 veya 6 ile başlar, 6 XX XX XX XX veya XX XXX XXXX
  if (dialCode === '31') {
    if (digits.length <= 2) return digits;
    if (digits.length <= 4) return `${digits.slice(0, 2)} ${digits.slice(2)}`;
    if (digits.length <= 6) return `${digits.slice(0, 2)} ${digits.slice(2, 4)} ${digits.slice(4)}`;
    if (digits.length <= 8) return `${digits.slice(0, 2)} ${digits.slice(2, 4)} ${digits.slice(4, 6)} ${digits.slice(6)}`;
    return `${digits.slice(0, 2)} ${digits.slice(2, 4)} ${digits.slice(4, 6)} ${digits.slice(6, 8)} ${digits.slice(8, 10)}`;
  }

  // Genel uluslararası gruplama (3-3-4 veya 2-3-3)
  if (digits.length <= 3) return digits;
  if (digits.length <= 6) return `${digits.slice(0, 3)} ${digits.slice(3)}`;
  if (digits.length <= 9) return `${digits.slice(0, 3)} ${digits.slice(3, 6)} ${digits.slice(6)}`;
  return `${digits.slice(0, 3)} ${digits.slice(3, 6)} ${digits.slice(6, 9)} ${digits.slice(9)}`;
};

/**
 * Tablolarda ve kartlarda görüntülemek için telefon numarasını (+31 6 12 34 56 78 gibi) güzelleştirir.
 */
export const formatDisplayPhone = (fullPhone = '') => {
  if (!fullPhone) return '-';
  const { dialCode, nationalNumber } = splitPhoneNumber(fullPhone);
  if (!nationalNumber) return fullPhone;
  const formatted = formatNationalPhoneNumber(nationalNumber, dialCode);
  return `+${dialCode} ${formatted}`;
};

// Local storage helpers
export const setLocalStorage = (key, value) => {
  try {
    localStorage.setItem(key, JSON.stringify(value));
  } catch (error) {
    console.error('Local storage error:', error);
  }
};

export const getLocalStorage = (key) => {
  try {
    const item = localStorage.getItem(key);
    return item ? JSON.parse(item) : null;
  } catch (error) {
    console.error('Local storage error:', error);
    return null;
  }
};

// Error handling
export const handleFirebaseError = (error) => {
  console.error('Firebase error:', error);

  switch (error.code) {
    case 'auth/user-not-found':
      return 'Bu email adresi ile kayıtlı kullanıcı bulunamadı.';
    case 'auth/wrong-password':
      return 'Şifre hatalı.';
    case 'auth/invalid-email':
      return 'Geçersiz email adresi.';
    case 'auth/too-many-requests':
      return 'Çok fazla başarısız giriş denemesi. Lütfen daha sonra tekrar deneyin.';
    case 'permission-denied':
      return 'Firebase erişim izni yok. Lütfen Firebase güvenlik kurallarını kontrol edin.';
    case 'unavailable':
      return 'Firebase servisi şu anda kullanılamıyor. Lütfen daha sonra tekrar deneyin.';
    default:
      return 'Bir hata oluştu. Lütfen tekrar deneyin.';
  }
};

// Loading states
export const createLoadingState = () => ({
  isLoading: false,
  error: null,
  data: null
});

// Debounce function
export const debounce = (func, wait) => {
  let timeout;
  return function executedFunction(...args) {
    const later = () => {
      clearTimeout(timeout);
      func(...args);
    };
    clearTimeout(timeout);
    timeout = setTimeout(later, wait);
  };
};

// Tül Hesaplama Fonksiyonları
export const calculatePileDensity = (pileModel) => {
  const densityValues = {
    'Standart SEYREK': 2,
    'Standart ORTA': 2.5,
    'Standart SIK': 3,
    'S Pile': 3,
    'Amerikan Pile Tekli': 1.8,
    'Amerikan Pile 2\'li': 2.2,
    'Amerikan Pile 3\'lü': 3.2,
    'Kapsül Pile': 3
  };

  return densityValues[pileModel] || 0;
};

export const calculateUsedM2 = (width, height, pileModel, customPileDensity = null, ortadanIkiParca = false) => {
  // Eğer custom pile sıklığı verilmişse onu kullan, yoksa pile modelinden hesapla
  let pileDensity = customPileDensity !== null ? parseFloat(customPileDensity) : calculatePileDensity(pileModel);

  // CM değerlerini metre'ye çevir (100'e böl)
  const widthNum = (parseFloat(width) || 0) / 100;

  let used = 0;
  if (!pileDensity || pileDensity === 0) {
    // Pile sıklığı olmayan modeller için
    switch (pileModel) {
      case 'Çima Dikiş':
        used = widthNum + 0.75;
        break;
      case 'Düz Dikiş':
        used = widthNum + 0.5;
        break;
      case 'Kumaş Güneşlik':
        used = widthNum + 0.5;
        break;
      default:
        used = widthNum;
        break;
    }
  } else {
    // Pile sıklığı olan modeller için
    used = (widthNum * pileDensity) + 0.5;
  }

  // Ortadan iki parça seçildiğinde kullanılan metreye 50 cm (0.50 m) ekle
  if (ortadanIkiParca) {
    used += 0.5;
  }

  return used;
};

/** Tül/Kanat pile ek ücretini ürün tipine göre döndürür (yeni alanlar öncelikli, eski ortak alanlara fallback). */
export const getTulKanatPileExtraPrice = (pileModel, adminSettings, productType) => {
  const isTul = productType === 'tul';
  const legacyMap = {
    'S Pile': adminSettings?.sPilePrice,
    'Amerikan Pile Tekli': adminSettings?.tulAmericanTekliPrice ?? adminSettings?.americanTekliPrice,
    "Amerikan Pile 2'li": adminSettings?.american2Price,
    "Amerikan Pile 3'lü": adminSettings?.american3Price,
  };
  const typedMap = isTul
    ? {
        'S Pile': adminSettings?.tulSPilePrice,
        'Amerikan Pile Tekli': adminSettings?.tulAmericanTekliPrice ?? adminSettings?.americanTekliPrice,
        "Amerikan Pile 2'li": adminSettings?.tulAmerican2Price,
        "Amerikan Pile 3'lü": adminSettings?.tulAmerican3Price,
      }
    : {
        'S Pile': adminSettings?.kanatSPilePrice,
        'Amerikan Pile Tekli': adminSettings?.kanatAmericanTekliPrice ?? adminSettings?.americanTekliPrice,
        "Amerikan Pile 2'li": adminSettings?.kanatAmerican2Price,
        "Amerikan Pile 3'lü": adminSettings?.kanatAmerican3Price,
      };

  const value = typedMap[pileModel] ?? legacyMap[pileModel];
  return Number(value) || 0;
};

/** Tül/Kanat kapsül pile birim fiyatını ürün tipine göre döndürür. */
export const getTulKanatKapsulPrice = (adminSettings, productType) => {
  if (productType === 'tul') return Number(adminSettings?.tulKapsulPrice) || 0;
  return Number(adminSettings?.kanatKapsulPrice) || 0;
};

/** Tül alt etek katlama fiyatını döndürür (sadece tül). */
export const getTulAltEtekKatlamaPrice = (adminSettings) => {
  return Number(adminSettings?.tulAltEtekKatlamaPrice) || 0;
};

const STOR_ZEBRA_EXTRA_PRICE_KEYS = {
  jumbo: 'JumboPrice',
  altEtekDilim: 'AltEtekDilimPrice',
  kapaliKasa: 'KapaliKasaPrice',
  altEtekLazer: 'AltEtekLazerPrice',
  dekoratifLama: 'DekoratifLamaPrice',
  dekoratifKasa: 'DekoratifKasaPrice',
  altEtekBeyazIs: 'AltEtekBeyazIsPrice',
};

/** Stor/Zebra ek ücret fiyatını ürün tipine göre döndürür. */
export const getStorZebraExtraPrice = (fieldKey, adminSettings, productType) => {
  const prefix = productType === 'stor' ? 'stor' : 'zebra';
  const suffix = STOR_ZEBRA_EXTRA_PRICE_KEYS[fieldKey];
  if (!suffix) return 0;
  return Number(adminSettings?.[`${prefix}${suffix}`]) || 0;
};

/** Ürün tipine göre motor ayarlarını döndürür (3 firma + kumanda desteği ve geriye dönük uyumluluk). */
export const getProductMotorSettings = (adminSettings = {}, productType) => {
  const prefix = productType === 'perde' ? 'dikeyPerde' : productType;
  const currency = adminSettings[`${prefix}MotorCurrency`] || 'EUR';

  // 3 Motor firması
  const brands = [
    {
      id: 'firma1',
      name: adminSettings[`${prefix}MotorFirma1Name`] || 'Firma 1',
      motorPrice: adminSettings[`${prefix}MotorFirma1Price`] !== undefined && adminSettings[`${prefix}MotorFirma1Price`] !== ''
        ? adminSettings[`${prefix}MotorFirma1Price`]
        : (adminSettings[`${prefix}MotorPrice`] || ''),
      kumandaPrice: adminSettings[`${prefix}MotorFirma1KumandaPrice`] !== undefined
        ? adminSettings[`${prefix}MotorFirma1KumandaPrice`]
        : ''
    },
    {
      id: 'firma2',
      name: adminSettings[`${prefix}MotorFirma2Name`] || 'Firma 2',
      motorPrice: adminSettings[`${prefix}MotorFirma2Price`] ?? '',
      kumandaPrice: adminSettings[`${prefix}MotorFirma2KumandaPrice`] ?? ''
    },
    {
      id: 'firma3',
      name: adminSettings[`${prefix}MotorFirma3Name`] || 'Firma 3',
      motorPrice: adminSettings[`${prefix}MotorFirma3Price`] ?? '',
      kumandaPrice: adminSettings[`${prefix}MotorFirma3KumandaPrice`] ?? ''
    }
  ];

  return {
    currency,
    price: adminSettings[`${prefix}MotorPrice`] || '',
    kumandaPrice: adminSettings[`${prefix}KumandaPrice`] || '',
    brands
  };
};

/** Stor/Zebra motor ayarlarını ürün tipine göre döndürür (Geriye dönük uyumluluk için). */
export const getStorZebraMotorSettings = (adminSettings, productType) => {
  return getProductMotorSettings(adminSettings, productType);
};


export const calculateCapsulePrice = (usedM2, capsuleUnitPrice) => {
  const usedM2Num = parseFloat(usedM2) || 0;
  const capsuleUnitPriceNum = parseFloat(capsuleUnitPrice) || 0;

  // Metrekareyi bir üst tam sayıya yuvarla
  const roundedM2 = Math.ceil(usedM2Num);

  // Metrekarede 7 kapsül kullanılıyor
  const capsuleCount = roundedM2 * 7;

  return capsuleCount * capsuleUnitPriceNum;
};

/** Kanat astar ücreti: kullanılan mt × birim fiyat × adet */
export const calculateAstarPrice = (usedM, astarUnitPrice, quantity = 1) => {
  const used = parseFloat(usedM) || 0;
  const unit = parseFloat(astarUnitPrice) || 0;
  const qty = parseFloat(quantity) || 1;
  return used * unit * qty;
};

export const calculateTotalPrice = (usedM2, unitPrice, extraPrice, pileModel, capsulePrice = 0) => {
  const usedM2Num = parseFloat(usedM2) || 0;
  const unitPriceNum = parseFloat(unitPrice) || 0;
  const extraPriceNum = parseFloat(extraPrice) || 0;
  const capsulePriceNum = parseFloat(capsulePrice) || 0;

  // Standart modeller - ek ücret varsa ekle
  if (['Standart SEYREK', 'Standart ORTA', 'Standart SIK'].includes(pileModel)) {
    return usedM2Num * (unitPriceNum + extraPriceNum);
  }

  // Dikiş modelleri - ek ücret varsa ekle
  if (['Çima Dikiş', 'Düz Dikiş', 'Kumaş Güneşlik'].includes(pileModel)) {
    return usedM2Num * (unitPriceNum + extraPriceNum);
  }

  // Ek ücretli modeller
  if (['S Pile', 'Amerikan Pile Tekli', 'Amerikan Pile 2\'li', 'Amerikan Pile 3\'lü'].includes(pileModel)) {
    return usedM2Num * (unitPriceNum + extraPriceNum);
  }

  // Kapsül Pile - her zaman kapsül ücreti ekle (ek ücret boş olsa bile)
  if (pileModel === 'Kapsül Pile') {
    const basePrice = usedM2Num * (unitPriceNum + extraPriceNum);
    return basePrice + capsulePriceNum;
  }

  return 0;
};

export const calculateTotalCost = (usedM2, unitCost) => {
  const usedM2Num = parseFloat(usedM2) || 0;
  const unitCostNum = parseFloat(unitCost) || 0;
  return usedM2Num * unitCostNum;
};

export const calculateDiscountedPrice = (totalPrice, discount) => {
  const totalPriceNum = parseFloat(totalPrice) || 0;
  const discountNum = parseFloat(discount) || 0;

  if (discountNum <= 0) return totalPriceNum;

  const discountAmount = totalPriceNum * (discountNum / 100);
  return totalPriceNum - discountAmount;
};

// Stor ve Zebra Hesaplama Fonksiyonları
export const calculateStorZebraUsedM2 = (width, height) => {
  const w = parseFloat(width) || 0;
  const h = parseFloat(height) || 0;
  if (w <= 0 || h <= 0) return 0;

  // CM değerlerini metre'ye çevir (100'e böl)
  let widthNum = w / 100;
  let heightNum = h / 100;

  // En için minimum 1 metre kontrolü; 1m'i geçince bir üst ondalık sayıya tamamla (0.1 katı)
  if (widthNum < 1) {
    widthNum = 1;
  } else {
    widthNum = Math.ceil(Number((widthNum * 10).toFixed(6))) / 10;
  }

  // Boy için minimum 2 metre kontrolü; 2m'i geçince en gibi bir üst ondalık sayıya tamamla (0.1 katı)
  if (heightNum < 2) {
    heightNum = 2;
  } else {
    heightNum = Math.ceil(Number((heightNum * 10).toFixed(6))) / 10;
  }

  return Number((widthNum * heightNum).toFixed(4));
};

export const calculateStorZebraTotalPrice = (usedM2, unitPrice, width, extras = {}, motorPriceTL = 0, mounting = 0) => {
  const usedM2Num = parseFloat(usedM2) || 0;
  const unitPriceNum = parseFloat(unitPrice) || 0;
  
  // Ekstra seçenekler için özel en hesaplaması
  let widthForExtras = (parseFloat(width) || 0) / 100; // cm -> metre
  if (widthForExtras > 0) {
    if (widthForExtras < 1) widthForExtras = 1;
    // 50cm (0.5m) katlarına yukarı yuvarla
    widthForExtras = Math.ceil(widthForExtras * 2) / 2;
  }

  const motorPriceTLNum = parseFloat(motorPriceTL) || 0;
  const mountingNum = parseFloat(mounting) || 0;

  const basePrice = usedM2Num * unitPriceNum;
  
  // Jumbo sistem en ile çarpılmaz, doğrudan adet başına eklenir. Diğer ekstralar ise hesaplanan en (widthForExtras) ile çarpılır.
  const jumboCost = parseFloat(extras.jumbo) || 0;

  const otherExtrasTotal = Object.entries(extras).reduce((sum, [key, val]) => {
    if (key === 'jumbo') return sum;
    const valNum = parseFloat(val) || 0;
    return sum + (valNum * widthForExtras);
  }, 0);

  return basePrice + otherExtrasTotal + jumboCost + motorPriceTLNum + mountingNum;
};

// Ahşap Jaluzi ve Metal Jaluzi Hesaplama Fonksiyonları
export const calculateJaluziUsedM2 = (width, height) => {
  // CM değerlerini metre'ye çevir (100'e böl)
  let widthNum = (parseFloat(width) || 0) / 100;
  let heightNum = (parseFloat(height) || 0) / 100;

  // En için minimum 1 metre kontrolü
  if (widthNum < 1) {
    widthNum = 1;
  }

  // Boy için minimum 1 metre kontrolü
  if (heightNum < 1) {
    heightNum = 1;
  }

  // 10'un katına yuvarlama
  widthNum = Math.ceil(widthNum * 10) / 10;
  heightNum = Math.ceil(heightNum * 10) / 10;

  return widthNum * heightNum;
};

export const calculateJaluziTotalPrice = (usedM2, unitPrice, mounting = 0, motorPriceTL = 0, zigilAmount = 0, ikiCamArasiAmount = 0) => {
  const usedM2Num = parseFloat(usedM2) || 0;
  const unitPriceNum = parseFloat(unitPrice) || 0;
  const mountingNum = parseFloat(mounting) || 0;
  const motorPriceTLNum = parseFloat(motorPriceTL) || 0;
  const zigilAmountNum = parseFloat(zigilAmount) || 0;
  const ikiCamArasiAmountNum = parseFloat(ikiCamArasiAmount) || 0;

  const basePrice = usedM2Num * unitPriceNum;

  return basePrice + mountingNum + motorPriceTLNum + zigilAmountNum + ikiCamArasiAmountNum;
};

// Dikey Perde Hesaplama Fonksiyonu
export const calculateDikeyPerdeTotalPrice = (usedM2, unitPrice, backgroundCurtain = 0, mounting = 0, motorPriceTL = 0) => {
  const usedM2Num = parseFloat(usedM2) || 0;
  const unitPriceNum = parseFloat(unitPrice) || 0;
  const backgroundCurtainNum = parseFloat(backgroundCurtain) || 0;
  const mountingNum = parseFloat(mounting) || 0;
  const motorPriceTLNum = parseFloat(motorPriceTL) || 0;

  const basePrice = usedM2Num * (unitPriceNum + backgroundCurtainNum);

  return basePrice + mountingNum + motorPriceTLNum;
};

// Plise Hesaplama Fonksiyonu
export const calculatePliseUsedM2 = (width, height) => {
  // CM değerlerini metre'ye çevir (100'e böl)
  let widthNum = (parseFloat(width) || 0) / 100;
  let heightNum = (parseFloat(height) || 0) / 100;

  // 10'un katına yuvarlama
  widthNum = Math.ceil(widthNum * 10) / 10;
  heightNum = Math.ceil(heightNum * 10) / 10;

  // İlk çarpım hesaplama
  const calculatedArea = widthNum * heightNum;

  // Eğer çarpım 1'den büyükse, değerleri aynen kullan
  // Eğer çarpım 1'den küçükse, direkt 1 olarak ayarla
  if (calculatedArea >= 1) {
    return calculatedArea;
  } else {
    return 1;
  }
};

export const calculatePliseTotalPrice = (usedM2, unitPrice1 = 0, unitPrice2 = 0, mounting = 0) => {
  const usedM2Num = parseFloat(usedM2) || 0;
  const unitPrice1Num = parseFloat(unitPrice1) || 0;
  const unitPrice2Num = parseFloat(unitPrice2) || 0;
  const mountingNum = parseFloat(mounting) || 0;

  // Toplam birim fiyat (iki ürünün toplamı)
  const totalUnitPrice = unitPrice1Num + unitPrice2Num;

  // Eğer m2 0 ise (ölçü girilmemişse) varsayılan 1 m2 üzerinden hesapla 
  // (Ancak birim fiyatlar 0 ise sonuç yine 0/montaj olacaktır)
  const effectiveM2 = usedM2Num > 0 ? usedM2Num : 1;

  const basePrice = effectiveM2 * totalUnitPrice;

  return basePrice + mountingNum;
};

// Aksesuar Hesaplama Fonksiyonu
export const calculateAksesuarTotalPrice = (unitPrice, quantity) => {
  const unitPriceNum = parseFloat(unitPrice) || 0;
  const quantityNum = parseFloat(quantity) || 0;

  return unitPriceNum * quantityNum;
};

// Katlamalı Perde Hesaplama Fonksiyonları
export const calculateKatlamaliPerdeUsedM = (width) => {
  // CM değerini metre'ye çevir ve 0.35 ekle
  const widthNum = (parseFloat(width) || 0) / 100;
  return widthNum + 0.35;
};

export const calculateKatlamaliPerdeTotalPrice = (usedM, fabricPrice = 0, mechanismPrice = 0, accessories = [], mounting = 0, motorPrice = 0, quantity = 1) => {
  const usedMNum = parseFloat(usedM) || 0;
  const fabricPriceNum = parseFloat(fabricPrice) || 0;
  const mechanismPriceNum = parseFloat(mechanismPrice) || 0;
  const mountingNum = parseFloat(mounting) || 0;
  const motorPriceNum = parseFloat(motorPrice) || 0;
  const qtyNum = parseFloat(quantity) || 1;

  // Aksesuar toplamını hesapla
  let totalAccessoryPrice = 0;
  if (Array.isArray(accessories)) {
    totalAccessoryPrice = accessories.reduce((sum, acc) => {
      const p = parseFloat(acc.price) || 0;
      const q = parseFloat(acc.quantity) || 0;
      return sum + (p * q);
    }, 0);
  }

  // Toplam Tutar = ((Kullanılan metre x (Kumaş + Mekanizma)) + Aksesuarlar + Montaj + Motor) x Adet
  const basePrice = usedMNum * (fabricPriceNum + mechanismPriceNum);
  return (basePrice + totalAccessoryPrice + mountingNum + motorPriceNum) * qtyNum;
};

export const calculateRustikUsedCm = (width) => {
  let widthNum = parseFloat(width) || 0;
  if (widthNum <= 0) return 0;
  
  // Minimum 120cm
  if (widthNum <= 120) return 120;
  
  // 40'ın katlarına yukarı yuvarla
  return Math.ceil(widthNum / 40) * 40;
};

// Ray Sistemi Hesaplama Fonksiyonları
export const calculateRaySistemiUsedMeter = (width, olcuTamamlama) => {
  let widthNum = (parseFloat(width) || 0) / 100; // cm -> m
  if (widthNum <= 0) return 0;

  if (olcuTamamlama === '25cm') {
    // 25cm (0.25m) katlarına yukarı yuvarla
    widthNum = Math.ceil(widthNum * 4) / 4;
  } else if (olcuTamamlama === '50cm') {
    // 50cm (0.5m) katlarına yukarı yuvarla
    widthNum = Math.ceil(widthNum * 2) / 2;
  }
  // urunBoyutu seçeneğinde yuvarlama yapma

  return widthNum;
};

export const calculateRustikTotalPrice = (unitPrice, quantity, ayakAdeti = 0, ekAyakSayisi = 0, ayakUcreti = 0, mounting = 0, rayOptions = [], accessories = [], usedM2 = 0) => {
  const upNum = parseFloat(unitPrice) || 0;
  const qtyNum = parseFloat(quantity) || 1;
  const ekAAdet = parseFloat(ekAyakSayisi) || 0;
  const aUcret = parseFloat(ayakUcreti) || 0;
  const mnt = parseFloat(mounting) || 0;
  const usedCm = parseFloat(usedM2) || 0;
  const usedMeters = usedCm / 100;

  // Birim fiyat direkt adetle çarpılır
  const basePrice = upNum * qtyNum;

  // Sadece ek ayaklar ücretlendirilir
  const ayakToplam = ekAAdet * aUcret;

  // Ray seçenekleri toplamı: Ray Fiyatı * Kullanılan Metre
  let totalRayPrice = 0;
  if (Array.isArray(rayOptions)) {
    totalRayPrice = rayOptions.reduce((sum, opt) => {
      const p = parseFloat(opt.price) || 0;
      return sum + (p * usedMeters);
    }, 0);
  }

  // Aksesuar toplamı: Fiyat * Adet
  let totalAccessoryPrice = 0;
  if (Array.isArray(accessories)) {
    totalAccessoryPrice = accessories.reduce((sum, acc) => {
      const p = parseFloat(acc.price) || 0;
      const q = parseFloat(acc.quantity) || 0;
      return sum + (p * q);
    }, 0);
  }

  // Toplam Tutar = (Birim Fiyat x Adet) + (Ek Ayak x Ayak Ücreti) + Ray Seçenekleri + Aksesuarlar + Montaj
  return basePrice + ayakToplam + totalRayPrice + totalAccessoryPrice + mnt;
};

// Dashboard İstatistik Hesaplama Fonksiyonları
export const calculateOrderStats = (orders) => {
  const now = new Date();
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const thisWeek = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
  const thisMonth = new Date(now.getFullYear(), now.getMonth(), 1);

  const stats = {
    total: orders.length,
    today: 0,
    thisWeek: 0,
    thisMonth: 0
  };

  orders.forEach(order => {
    // Sipariş tarihi varsa onu kullan, yoksa createdAt kullan
    const dateToUse = order.orderDate || order.createdAt;
    const createdAt = dateToUse?.toDate() || new Date(dateToUse);

    if (createdAt >= today) {
      stats.today++;
    }
    if (createdAt >= thisWeek) {
      stats.thisWeek++;
    }
    if (createdAt >= thisMonth) {
      stats.thisMonth++;
    }
  });

  return stats;
};

export const calculateRevenueStats = (orders) => {
  const now = new Date();
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const thisWeek = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
  const thisMonth = new Date(now.getFullYear(), now.getMonth(), 1);

  const stats = {
    total: 0,
    today: 0,
    thisWeek: 0,
    thisMonth: 0,
    average: 0
  };

  orders.forEach(order => {
    // İndirimli tutar varsa onu kullan, yoksa toplam tutarı kullan
    const orderAmount = order.discountedPrice && parseFloat(order.discount) > 0
      ? parseFloat(order.discountedPrice || 0)
      : parseFloat(order.totalPrice || 0);

    stats.total += orderAmount;

    // Siparişler için createdAt kullan
    const createdAt = order.createdAt?.toDate() || new Date(order.createdAt);

    if (createdAt >= today) {
      stats.today += orderAmount;
    }
    if (createdAt >= thisWeek) {
      stats.thisWeek += orderAmount;
    }
    if (createdAt >= thisMonth) {
      stats.thisMonth += orderAmount;
    }
  });

  stats.average = orders.length > 0 ? stats.total / orders.length : 0;

  return stats;
};

export const calculateProductTypeDistribution = (orders) => {
  const distribution = {};

  orders.forEach(order => {
    const productType = order.productType || 'unknown';
    distribution[productType] = (distribution[productType] || 0) + 1;
  });

  return distribution;
};

export const calculateOrderStatusDistribution = (customers) => {
  const distribution = {};

  // ORDER_STATUS_LABELS'dan tüm durumları al
  Object.values(ORDER_STATUS_LABELS).forEach(label => {
    distribution[label] = 0;
  });

  customers.forEach(customer => {
    const status = customer.orderStatus || 'new_order';
    const statusLabel = ORDER_STATUS_LABELS[status] || 'Yeni Sipariş';
    distribution[statusLabel] = (distribution[statusLabel] || 0) + 1;
  });

  return distribution;
};

export const calculateMonthlyCustomerGrowth = (customers) => {
  const monthlyData = {};

  customers.forEach(customer => {
    // Sipariş tarihi varsa onu kullan, yoksa createdAt kullan
    const dateToUse = customer.orderDate || customer.createdAt;
    const createdAt = dateToUse?.toDate() || new Date(dateToUse);
    const monthKey = `${createdAt.getFullYear()}-${String(createdAt.getMonth() + 1).padStart(2, '0')}`;

    monthlyData[monthKey] = (monthlyData[monthKey] || 0) + 1;
  });

  return monthlyData;
};

export const calculateMonthlyRevenue = (orders) => {
  const monthlyData = {};

  orders.forEach(order => {
    // Siparişler için createdAt kullan
    const createdAt = order.createdAt?.toDate() || new Date(order.createdAt);
    const monthKey = `${createdAt.getFullYear()}-${String(createdAt.getMonth() + 1).padStart(2, '0')}`;

    const orderAmount = order.discountedPrice && parseFloat(order.discount) > 0
      ? parseFloat(order.discountedPrice || 0)
      : parseFloat(order.totalPrice || 0);

    monthlyData[monthKey] = (monthlyData[monthKey] || 0) + orderAmount;
  });

  return monthlyData;
}; 